/** Stage E3.1 — Fundamentals + News panels (TanStack Query — LONG/MEDIUM stale) */
import { Loader2, ExternalLink, TrendingUp, TrendingDown, Minus } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { keys } from "@/lib/queryKeys";
import { STALE, GC } from "@/lib/queryClient";
import { formatLocalDateISO } from "@/lib/dates";

function fmtNum(v) {
  if (v == null) return "—";
  const n = Number(v);
  if (Math.abs(n) >= 1e12) return `${(n / 1e12).toFixed(2)}T`;
  if (Math.abs(n) >= 1e9)  return `${(n / 1e9).toFixed(2)}B`;
  if (Math.abs(n) >= 1e6)  return `${(n / 1e6).toFixed(2)}M`;
  if (Math.abs(n) >= 1e3)  return `${(n / 1e3).toFixed(2)}K`;
  return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

function fmtPct(v) {
  if (v == null) return "—";
  const n = Number(v);
  return `${(Math.abs(n) < 1 ? n * 100 : n).toFixed(2)}%`;
}

function Row({ label, cell, format = "num", tone }) {
  const status = cell?.status || "unavailable";
  const value = cell?.value;
  const display = status !== "available" ? status.toUpperCase()
    : format === "pct" ? fmtPct(value)
    : format === "raw" ? String(value)
    : fmtNum(value);
  return (
    <div className="grid grid-cols-2 gap-2 py-1 text-xs border-b border-border/30">
      <span className="text-muted-foreground uppercase tracking-wide text-[10px]">{label}</span>
      <span className={`mono text-right ${status !== "available" ? "text-muted-foreground italic text-[10px]" : (tone || "")}`}>{display}</span>
    </div>
  );
}

export function FundamentalsPanel({ symbol }) {
  const { data: f, isLoading: loading, isError, refetch } = useQuery({
    queryKey: keys.fundamentals(symbol),
    queryFn: async () => (await api.get(`/market-intel/fundamentals/${symbol}`)).data,
    enabled: !!symbol,
    staleTime: STALE.LONG,
    gcTime: GC.LONG,
  });

  if (loading) return <div className="h-32 flex items-center text-xs text-muted-foreground"><Loader2 size={12} className="animate-spin mr-2"/>Loading…</div>;
  if (isError) return (
    <div className="border border-dashed border-signal-red/40 p-4 text-center text-xs text-muted-foreground" role="alert">
      Failed to load fundamentals. <button onClick={() => refetch()} className="underline underline-offset-2 hover:text-foreground focus:outline-none focus:ring-1 focus:ring-current" data-testid="fundamentals-retry">Retry</button>
    </div>
  );
  if (!f?.available) return <div className="border border-dashed border-border/40 p-4 text-center text-xs text-muted-foreground">Fundamentals unavailable for {symbol}.</div>;

  return (
    <div className="space-y-2" data-testid="fundamentals-panel">
      <div className="label-cap">Fundamentals · {symbol}</div>
      <Row label="Market cap" cell={f.market_cap}/>
      <Row label="Revenue (TTM)" cell={f.revenue}/>
      <Row label="Revenue growth YoY" cell={f.revenue_growth_yoy} format="pct"/>
      <Row label="EPS (TTM)" cell={f.eps}/>
      <Row label="Earnings growth YoY" cell={f.earnings_growth_yoy} format="pct"/>
      <Row label="P/E trailing" cell={f.pe_trailing}/>
      <Row label="P/E forward" cell={f.pe_forward}/>
      <Row label="PEG" cell={f.peg}/>
      <Row label="Free cash flow" cell={f.free_cash_flow}/>
      <Row label="Total debt" cell={f.total_debt}/>
      <Row label="Debt / equity" cell={f.debt_to_equity}/>
      <Row label="ROE" cell={f.roe} format="pct"/>
      <Row label="Gross margin" cell={f.gross_margin} format="pct"/>
      <Row label="Operating margin" cell={f.operating_margin} format="pct"/>
      <Row label="Dividend yield" cell={f.dividend_yield} format="pct"/>
      <Row label="Beta" cell={f.beta}/>
      <Row label="Sector" cell={f.sector} format="raw"/>
      <Row label="Industry" cell={f.industry} format="raw"/>
    </div>
  );
}

function SentimentIcon({ s }) {
  if (s === "positive") return <TrendingUp size={11} className="pnl-up"/>;
  if (s === "negative") return <TrendingDown size={11} className="pnl-down"/>;
  return <Minus size={11} className="text-muted-foreground"/>;
}

export function NewsPanel({ symbol, decisionDate }) {
  const { data, isLoading: loading, isError, refetch } = useQuery({
    queryKey: keys.news(symbol),
    queryFn: async () => (await api.get(`/market-intel/news/${symbol}`, { params: { limit: 15 } })).data,
    enabled: !!symbol,
    staleTime: STALE.MEDIUM,
    gcTime: GC.MEDIUM,
  });
  const items = data?.items || [];
  const available = !!data?.available;

  if (loading) return <div className="h-32 flex items-center text-xs text-muted-foreground"><Loader2 size={12} className="animate-spin mr-2"/>Loading news…</div>;
  if (isError) return (
    <div className="border border-dashed border-signal-red/40 p-4 text-center text-xs text-muted-foreground" role="alert">
      Failed to load news. <button onClick={() => refetch()} className="underline underline-offset-2 hover:text-foreground focus:outline-none focus:ring-1 focus:ring-current" data-testid="news-retry">Retry</button>
    </div>
  );
  if (!available || items.length === 0) return <div className="border border-dashed border-border/40 p-4 text-center text-xs text-muted-foreground">No recent news for {symbol}.</div>;

  return (
    <div className="space-y-2" data-testid="news-panel">
      <div className="label-cap flex items-center gap-2">News · {symbol}
        {decisionDate && <span className="ml-auto mono text-[10px] text-muted-foreground normal-case" title={decisionDate}>Decision date: {formatLocalDateISO(decisionDate)} · hindsight highlighted</span>}
      </div>
      {items.map((n, i) => {
        const isBefore = decisionDate && n.published_at && n.published_at < decisionDate;
        const isAfter  = decisionDate && n.published_at && n.published_at > decisionDate;
        return (
          <a
            key={i} href={n.link} target="_blank" rel="noreferrer"
            className={`block border p-2 hover:bg-secondary/30 transition-colors ${isAfter ? "border-signal-yellow/40 bg-signal-yellow/5" : "border-border/40"}`}
            data-testid={`news-item-${i}`}
          >
            <div className="flex items-baseline gap-2 mb-1">
              <SentimentIcon s={n.sentiment}/>
              <span className={`chip !text-[9px] border-current ${n.materiality === "high" ? "text-signal-red" : "text-muted-foreground"}`}>{n.materiality}</span>
              <span className="mono text-[10px] text-muted-foreground">{n.publisher || ""}</span>
              <span className="ml-auto mono text-[10px] text-muted-foreground" title={n.published_at}>{formatLocalDateISO(n.published_at)}</span>
              {isAfter && <span className="chip !text-[9px] border-current text-signal-yellow">AFTER DECISION</span>}
              {isBefore && <span className="chip !text-[9px] border-current text-muted-foreground">BEFORE</span>}
            </div>
            <div className="text-xs text-foreground/90">{n.title}</div>
            {n.summary && <div className="text-[11px] text-muted-foreground mt-1">{n.summary.slice(0, 240)}{n.summary.length > 240 ? "…" : ""}</div>}
            <div className="mt-1 mono text-[10px] text-muted-foreground flex items-center gap-1"><ExternalLink size={9}/> open</div>
          </a>
        );
      })}
    </div>
  );
}
